import { isApiError } from "./client";

/**
 * There is no H3 mode that can preserve an arbitrary Animate source while
 * conditioning it with the immutable voice anchor. This copy is deliberately
 * shared by the preflight and the typed 409: the capability has the same
 * outcome whether the browser sees it first or the service refuses it.
 */
export const SOURCE_FIDELITY_UNAVAILABLE_COPY =
  "Animate cannot keep the selected image faithful while using the anchored voice. Your spoken line is still here. Explicitly remove it for a silent Animate video, or choose References for the anchored voice.";

/**
 * Server error codes as our prose, in one table.
 *
 * There were three of these, and they had already drifted: two different
 * sentences for the same code, in two files, neither knowing about the other.
 * That matters more here than it looks, because one of these sentences is a
 * security boundary — see `notFound` below.
 */
const CODE_COPY: Record<string, string> = {
  owner_required: "Only an owner can change members.",
  last_owner:
    "An organization has to keep at least one owner. Promote someone else first, then try this again.",
  staff_only: "Not available for this account.",
  unauthenticated: "Your session ended. Reload the page to sign in again.",
  store_unavailable:
    "Character Studio can't reach its database right now. Nothing was changed — try again in a minute.",
  network_error:
    "Character Studio could not reach the service. Your work has not been changed.",
  cross_site: "That request was blocked for security. Reload and try again.",
  unsupported_media_type:
    "The server rejected that request. Reload and try again.",
  characters_workspace_not_provisioned:
    "Characters isn't set up for this organization yet. Nothing was changed.",
  vision_unavailable:
    "Character analysis is unavailable right now. Existing work remains recorded; try again when the service is available.",
  composition_unavailable:
    "This workspace cannot prepare a composition right now. Try again when the service is available.",
  image_invalid:
    "That file could not be read as a JPEG or PNG image. Choose a supported image and try again.",
  image_too_large:
    "Choose an image with no more than 32 megapixels and 8,192 pixels per side.",
  upload_too_large: "Choose an image file no larger than 32 MiB.",
};

/**
 * Prose for a failed request.
 *
 * `notFound` is a required argument rather than a table entry because it is
 * the one sentence that can leak. The API answers the same 404 for "no such
 * organization" and "not yours", so this copy must read as AVAILABILITY and
 * never as permission: "you are not a member of this organization" confirms
 * the organization exists, which is exactly what the shared 404 is there to
 * withhold. It is also the only sentence whose subject changes with the
 * caller — an organization, a character, a media item — so the surface naming
 * that subject is the surface that must write it.
 */
export function errorCopy(
  error: unknown,
  copy: { notFound: string; fallback: string },
): string {
  if (!isApiError(error)) return copy.fallback;
  if (error.code === "not_found") return copy.notFound;
  if (error.status === 409 && error.code === "source_fidelity_unavailable") {
    return SOURCE_FIDELITY_UNAVAILABLE_COPY;
  }
  return CODE_COPY[error.code] ?? copy.fallback;
}
